import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import TextInput from '../common/InputWithLabel';
import LoginValidator from '../../helpers/validators/login.validator';
import useInput from '../../hooks/useInput';
import AuthenticationDispatch from '../../contexts/authenticationDispatch';
import authenticationActions from '../../actions/authentication.actions';
import Error from '../common/Error';
import SubmitButton from '../common/PrimaryButton';
import findErrorField from '../../helpers/serverError';

import style from './LoginForm.module.css';

const LoginForm = React.memo(() => {
  const emailAddress = useInput('');
  const password = useInput('');
  const [errors, setErrors] = useState({ emailAddress: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [authenticationRequested, setAuthenticationRequested] = useState(false);
  const dispatch = useContext(AuthenticationDispatch);

  useEffect(() => {
    const handleError = ({ response }) => {
      if (response && response.data && response.data.ValidationErrors) {
        const emailAddressError = findErrorField(
          response.data.ValidationErrors,
          'EmailAddress',
        );

        const passwordError = findErrorField(
          response.data.ValidationErrors,
          'Password',
        );

        setErrors({
          emailAddress: emailAddressError.Messages[0],
          password: passwordError.Messages[0],
        });
      } else {
        setServerError(
          'Sorry, the username or password is not valid. Please contact your help desk for assistance.',
        );
      }
    };

    const handleSuccess = ({ data }) => {
      localStorage.setItem('token', data.Token);
      localStorage.setItem('refresh', data.RefreshToken);
      localStorage.setItem('agencyId', data.Agency.AgencyId);

      dispatch({
        type: authenticationActions.AUTHENTICATION_SUCCESS,
      });
    };

    if (authenticationRequested) {
      axios
        .post(`${process.env.REACT_APP_BASE_URL}account/login`, {
          EmailAddress: emailAddress.value,
          Password: password.value,
        })
        .then(handleSuccess)
        .catch(handleError);

      setAuthenticationRequested(false);
    }
  }, [authenticationRequested, dispatch, emailAddress.value, password.value]);

  const onSubmit = event => {
    event.preventDefault();

    const validation = LoginValidator.validate({
      emailAddress: emailAddress.value,
      password: password.value,
    });

    setErrors({
      emailAddress: validation.emailAddress.message,
      password: validation.password.message,
    });

    if (validation.isValid) {
      setAuthenticationRequested(true);
    }
  };

  const onNewUser = event => {
    event.preventDefault();

    dispatch({
      type: authenticationActions.NEW_USER_REQUEST,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <TextInput
        type="text"
        name="emailAddress"
        label="Email Address"
        autocomplete="off"
        error={errors.emailAddress}
        data-testid="login-page-email-input"
        {...emailAddress}
      />
      <TextInput
        type="password"
        name="password"
        label="Password"
        autocomplete="off"
        error={errors.password}
        data-testid="login-page-password-input"
        {...password}
      />
      {serverError && <Error error={serverError} />}
      <div className={style.submitGroup}>
        <Link to="/login/forgot-password" className={style.forgotPassword}>
          Forgot Password?
        </Link>
        <SubmitButton text="Log In" data-testid="login-page-login-button" />
        <a href="new-user" onClick={onNewUser} className={style.newUser}>
          New user?
        </a>
      </div>
    </form>
  );
});

export default LoginForm;
